import { IntlShape } from '@formatjs/intl'
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { FormatXMLElementFn, PrimitiveType } from 'intl-messageformat'
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { Options as IntlMessageFormatOptions } from 'intl-messageformat/src/core'
import * as React from 'react'
import { useMemo, useRef } from 'react'
import { MessageDescriptor } from 'react-intl'
import { useIntlContext } from '../components/IntlProviders.context'
import TranslationFallbackWrapper from './TranslationFallbackWrapper'
import { ExecuteMessage } from './valeyard/reactRuntime'
import { Catalog, executeMessage, Expression } from './valeyard/runtime'

// For some reason the exported `MessageDescriptor` has the id as optional even
// though that is not valid and will cause formatMessage() to throw :(
export interface SaferMessageDescriptor extends MessageDescriptor {
  id: string
}

type MessageDescriptorWithFallback = SaferMessageDescriptor & {
  noProviderFoundMsg?: string
}

export type MsgProps<Values extends Record<string, any> = Record<string, React.ReactNode>> = Values &
  SaferMessageDescriptor & {
    tagName?: React.ElementType<any>
    children?(nodes: ReadonlyArray<React.ReactNode>): React.ReactElement | null
    ignoreTag?: IntlMessageFormatOptions['ignoreTag']
    values?: Values
  }

/**
 * Convenient way to add international messages using a Component.
 *
 * Benefits:
 * - Supports ICU Valeyard messages, unlike Format.js FormattedMessage
 * - values can be top-level props
 *
 * @example Simple message (count defaults to 1)
 * <Msg id="term.job" count={1} /> // renders "Job"
 *
 * @example Simple message with specific count
 * <Msg id="term.job" count={99} /> // renders "Jobs"
 *
 * @example Message with nested `term` value
 * <Msg id="common.ui.end_term" term={<Msg id="term.job" count={1} />} /> // renders "End Job"
 *
 * @example Message with a noProviderFoundMsg in case the provider is missing (good for elegant fallbacks without JS errors)
 * // Don't overuse this, as it hides areas that need to be wrapped. Save this for heavily used common components.
 * <Msg id="term.job" noProviderFoundMsg="Job" />
 */
export function Msg<Values extends Record<string, any> = Record<string, React.ReactNode>>(props: MsgProps<Values>) {
  return (
    <TranslationFallbackWrapper id={props.id} message={props.noProviderFoundMsg}>
      <InnerMsg {...props} />
    </TranslationFallbackWrapper>
  )
}

// Extracted InnerMsg out so `TranslationFallbackWrapper` can catch the provider not found error from `useIntlContext`
function InnerMsg<Values extends Record<string, any> = Record<string, React.ReactNode>>({
  id,
  description,
  defaultMessage,
  noProviderFoundMsg,
  tagName,
  ignoreTag,
  children,
  values: valuesOldProp,
  ...valuesConvenientTopLevelProps
}: MsgProps<Values>) {
  // TODO what do we want to do with these unused props??
  // const passThroughProps = { id, description, defaultMessage, tagName, ignoreTag, children }

  const values = { ...valuesOldProp, ...valuesConvenientTopLevelProps }
  const { intl, messages } = useIntlContext()
  const catalogFn = (_locale: string, messageKey: string) => messages[messageKey]

  return <ExecuteMessage messageKey={id} intl={intl} catalog={catalogFn} context={values} />
}

type DefaultValues = Record<string, PrimitiveType | FormatXMLElementFn<string, string> | PrimitiveType[]>

/**
 * Convenient way to add international messages using a hook and function. Use the <Msg> component if possible.
 *
 * @example Simple message (count defaults to 1)
 * const msg = useMsg()
 * msg("term.job", { count: 1 }) // renders "Job"
 *
 * @example Simple message with specific count
 * const msg = useMsg()
 * msg("term.job", { count: 99 }) // renders "Jobs"
 *
 * @example Message with nested `term` value
 * const msg = useMsg()
 * msg("common.ui.end_term", { term: msg("term.job", { count: 1 }) }) // renders "End Job"
 *
 *
 */
export function useMsg() {
  const intlRef = useRef<IntlShape>(undefined)
  const messagesRef = useRef<Record<string, Expression>>(undefined)
  const msgFnRef = useRef<typeof msg>(undefined)
  const hasLoggedErrorRef = useRef(false)
  let allLayersLoaded = false

  // If the valeyard ctx is loaded, use it. Otherwise, fallback to the default msg function
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const ctx = useIntlContext()
    intlRef.current = ctx.intl
    messagesRef.current = ctx.messages
    allLayersLoaded = ctx.allLayersLoaded

    if (!allLayersLoaded) {
      msgFnRef.current = msgContextLoading
    } else {
      msgFnRef.current = msg
    }
  } catch {
    msgFnRef.current = msgNoContextFound
  }

  // We want the returned function to be a stable reference, so we use useMemo
  return useMemo(() => {
    function msgFn(...args: Parameters<typeof msg>) {
      return msgFnRef.current!(...args)
    }

    msgFn.isLoaded = allLayersLoaded

    return msgFn
  }, [allLayersLoaded])

  function msg<Values extends Record<string, any> = DefaultValues>(
    id: string | SaferMessageDescriptor | React.ReactNode,
    values: Values = {} as Values
  ): string {
    // Prevent this function from throwing and crashing the whole page if invalid id is supplied (i.e. a falsey)
    if (!id || (typeof id !== 'string' && !isMessageDescriptor(id) && !React.isValidElement(id))) {
      // eslint-disable-next-line no-console
      console.error(`Warning: Invalid \`id\` of ${id} supplied to useMsg().msg(). Falling back to empty string.`)
      return ''
    }

    // If it's a string or a message descriptor, execute the expression
    if (typeof id === 'string' || isMessageDescriptor(id)) {
      const msgId = isMessageDescriptor(id) ? id?.id : id
      if (!messagesRef.current || !intlRef.current) return msgNoContextFound(id)
      const catalogFn: Catalog = (_locale: string, messageKey: string) => messagesRef.current![messageKey]
      const evaluatedMsg = executeMessage(msgId, intlRef.current, catalogFn, values)
      return evaluatedMsg || msgId
    }

    // If we get here, it's a React element, so we need to recurse through it and replace any nested Msgs
    const { id: lazyId, ...props } = id.props as Record<string, unknown>
    return msg(
      lazyId as string,
      Object.fromEntries(
        Object.entries(props).map(([key, value]) => [key, React.isValidElement(value) ? msg(value) : value])
      ) as Values
    )
  }

  function msgContextLoading() {
    return ''
  }

  function msgNoContextFound(id: string | SaferMessageDescriptor | React.ReactNode): string {
    let noProviderFoundMsg =
      (id as MessageDescriptorWithFallback)?.noProviderFoundMsg ?? (id as MessageDescriptorWithFallback)?.id
    if (noProviderFoundMsg === undefined && typeof id === 'string') noProviderFoundMsg = id
    if (!hasLoggedErrorRef.current) {
      // eslint-disable-next-line no-console
      console.error(
        'Translation intl obj not found, fallback of',
        noProviderFoundMsg,
        'used for id:',
        (id as MessageDescriptor)?.id ?? id
      )
      hasLoggedErrorRef.current = true
    }
    return String(noProviderFoundMsg)
  }
}

export function isMessageDescriptor(id: any): id is SaferMessageDescriptor {
  return id && typeof id !== 'string' && 'id' in id && id.id !== undefined
}

export type FormatMsg = ReturnType<typeof useMsg>

/**
 * A util to define msgs outside the context of React, portable and convenient.
 *
 * @example
 * // Define a lazy msg, possibly in an external js config
 * const myConfig = {
 *   tooltip: lazyMsg('page.tooltip', { count: 1 }),
 *   text: lazyMsg('page.text', { name: lazyMsg('page.text.name') })
 * }
 *
 * // In your component, use the lazy messages in either string or JSX contexts
 * const MyCmp = () => {
 *   const msg = useMsg()
 *   return (
 *     <Box title={msg(myConfig.tooltip)}>{myConfig.text}</Box>
 *   )
 * }
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export function lazyMsg<Values extends Record<string, React.ReactNode | {}> = DefaultValues>(
  id: string,
  values: Values = {} as Values,
  opts?: IntlMessageFormatOptions
) {
  const element = <Msg id={id} {...values} {...opts} />
  if (process.env.NODE_ENV === 'production') return element

  // React 19's dev build attaches a `console.createTask()` owner-stack task to
  // every element (`element._debugTask`) and calls `_debugTask.run()` while
  // rendering it. lazyMsg elements are created once at module scope and reused
  // across renders (they live in static config objects), and running that
  // module-scope task during render throws "'run' called with illegal receiver"
  // in Chromium once DevTools is open — blanking the page. Production builds
  // don't attach `_debugTask`, so this only bites in dev.
  //
  // The spread already drops `_debugTask` today because React defines it as a
  // non-enumerable property (spread copies only enumerable own props), so React
  // sees no task and skips the `.run()` call. The explicit reset is kept so this
  // keeps working if React ever makes `_debugTask` enumerable — do not remove it.
  //
  // Same error + root cause (React 19 dev, stored/reused JSX):
  // https://github.com/ant-design/ant-design/issues/55207
  const safeElement = { ...element } as typeof element & { _debugTask?: unknown }
  safeElement._debugTask = undefined
  return safeElement
}

/**
 *
 *
 *
 * EVERYTHING BELOW IS DEPRECATED
 *
 *
 *
 */

export type Values = Record<string, MessageConfig>
export type MessageConfig = { id: string; values?: Values; toLowerCase?: boolean } | string | number | null

/**
 * Custom error class that can hold a non-string message.
 *
 * This is useful for localization (L10n) scenarios where the error
 * message might be an object or structure that needs further processing
 * before being displayed to the user in their language.
 *
 * @template T The type of the original message payload. Defaults to `unknown`.
 */
export class L10nError<T = unknown> extends Error {
  /**
   * The original error message object (can be any type).
   */
  public readonly originalMessage: T

  /**
   * Creates an instance of L10nError.
   *
   * @param message - The error message, which can be of any type.
   */
  constructor(message: T) {
    // Generate a default string message for the base Error class.
    // We use JSON.stringify for objects/arrays, otherwise convert to string.
    // This ensures compatibility with standard error logging.
    let stringMessage: string
    if (typeof message === 'string') {
      stringMessage = message
    } else if (message !== null && typeof message === 'object') {
      try {
        stringMessage = `L10nError with object message: ${JSON.stringify(message)}`
      } catch {
        stringMessage = 'L10nError with non-serializable object message'
      }
    } else {
      stringMessage = `L10nError with message: ${String(message)}`
    }

    // Call the parent Error constructor
    super(stringMessage)

    // Set the prototype explicitly (standard practice when extending built-in types like Error)
    Object.setPrototypeOf(this, L10nError.prototype)

    // Set the error name
    this.name = 'L10nError'

    // Store the original message
    this.originalMessage = message
  }

  /**
   * Provides a string representation based on the original message type.
   * If the original message has a `toString` method, it's used.
   *
   * @returns A string representation of the error.
   */
  toString(): string {
    if (
      this.originalMessage &&
      typeof (this.originalMessage as any).toString === 'function' &&
      typeof this.originalMessage !== 'object'
    ) {
      // Use the original message's toString if it's meaningful (and not the default Object.toString)
      return String(this.originalMessage)
    }
    // Fallback to the message generated for the base Error class
    return this.message
  }
}

/**
 * A utility function to extract the 'region' part out of a locale string, e.g. 'FR' out of 'fr_FR'.
 * For locale string without the appropriate 'region' part, the return will be default to undefined.
 * @param localeStr
 */
export const get_locale_region = (localeStr: string | undefined) => {
  if (localeStr) {
    const parts = localeStr.replace('_', '-').split('-')
    const partsCount = parts.length
    // Only for locale strings not like 'en', 'en_001', etc.
    if (partsCount > 1) {
      const lastPart = parts[partsCount - 1]
      if (/^[A-Za-z]{2}$/.test(lastPart)) {
        return lastPart.toUpperCase()
      }
    }
  }
  return undefined // catch-all default
}
/**
 * Given a country's alpha-2-code, e.g. 'US', and the locale, e.g. 'fr-FR'. returns the name of the country in that locale, e.g.
 * 'États-Unis' is the name of USA in locale 'fr-FR'.
 * @param alpha_2_code
 * @param locale
 */
export const get_country_name_in_locale = (alpha_2_code: string, locale: string): string | undefined => {
  const regionNames = new Intl.DisplayNames([locale], { type: 'region' })
  return regionNames.of(alpha_2_code)
}
